import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { listBunkerReports, listNoonReports } from '../api/operations';
import { listVessels } from '../api/fleet';

const CO2_FACTOR_MT_PER_MT_FUEL = 3.114;

function asNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatVesselName(name) {
  return String(name || '').trim().replace(/\s+/g, ' ').toLowerCase().replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

function monthValue(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function reportData(report) {
  return report?.parsed_data || report?.parsedData || report || {};
}

function reportVessel(report) {
  const data = reportData(report);
  return formatVesselName(data.vesselName || data.vessel || report?.vessel_name || report?.vessel || report?.vessels?.name);
}

function reportDate(report) {
  const data = reportData(report);
  const value = data.reportDate || data.report_date || report?.report_date || report?.created_at || report?.createdAt;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function sameMonth(date, month) {
  if (!date || !month) return true;
  return monthValue(date) === month;
}

function bunkerFuelLines(bunker) {
  if (Array.isArray(bunker?.fuels) && bunker.fuels.length) return bunker.fuels;
  return [{
    grade: bunker?.grade,
    quantity: bunker?.quantity,
    pricePerMT: bunker?.pricePerMT,
  }];
}

function bunkerDate(bunker) {
  const value = bunker?.deliveryDate || bunker?.delivery_date || bunker?.created_at || bunker?.createdAt;
  const parsed = new Date(value);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function currency(value) {
  return '$' + Math.round(asNumber(value)).toLocaleString();
}

function number(value, digits = 0) {
  return asNumber(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

function ciiGrade(score) {
  if (!score) return 'TBC';
  if (score <= 6) return 'A';
  if (score <= 8) return 'B';
  if (score <= 10) return 'C';
  if (score <= 12) return 'D';
  return 'E';
}

function MetricCard({ label, value, sub, accent }) {
  return (
    <div style={{ background:'var(--s1)', border:'1px solid var(--b1)', borderRadius:'var(--radius)', padding:12 }}>
      <div style={{ fontSize:8, color:'var(--t3)', letterSpacing:1.4, textTransform:'uppercase', marginBottom:5 }}>{label}</div>
      <div style={{ fontFamily:'var(--syne)', fontSize:20, fontWeight:700, color:accent ? 'var(--accent)' : 'var(--text)', lineHeight:1.1 }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:'var(--t2)', marginTop:5 }}>{sub}</div>}
    </div>
  );
}

function ChartPanel({ title, subtitle, children }) {
  return (
    <div style={{ background:'var(--s1)', border:'1px solid var(--b1)', borderRadius:'var(--radius)', padding:12, minHeight:290 }}>
      <div style={{ display:'flex', justifyContent:'space-between', gap:10, alignItems:'baseline', marginBottom:10 }}>
        <div style={{ fontFamily:'var(--syne)', fontSize:13, fontWeight:700 }}>{title}</div>
        {subtitle && <div style={{ fontSize:10, color:'var(--t3)' }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

export default function Performance() {
  const [vessels, setVessels] = useState([]);
  const [noonReports, setNoonReports] = useState([]);
  const [bunkerReports, setBunkerReports] = useState([]);
  const [month, setMonth] = useState(monthValue());
  const [selectedVessel, setSelectedVessel] = useState('ALL');
  const [status, setStatus] = useState('Loading performance data...');

  useEffect(() => {
    let mounted = true;
    Promise.all([
      listVessels().catch(() => []),
      listNoonReports().catch(() => []),
      listBunkerReports().catch(() => []),
    ]).then(([fleet, noons, bunkers]) => {
      if (!mounted) return;
      setVessels(fleet || []);
      setNoonReports(noons || []);
      setBunkerReports(bunkers || []);
      setStatus('Performance data loaded');
    }).catch((error) => {
      if (mounted) setStatus(`Performance data unavailable: ${error.message || 'unknown error'}`);
    });
    return () => { mounted = false; };
  }, []);

  const model = useMemo(() => {
    const vesselNames = new Set((vessels || []).map((item) => formatVesselName(item.name)).filter(Boolean));
    (noonReports || []).forEach((report) => {
      const name = reportVessel(report);
      if (name) vesselNames.add(name);
    });
    (bunkerReports || []).forEach((bunker) => {
      const name = formatVesselName(bunker.vessel);
      if (name) vesselNames.add(name);
    });

    const selected = selectedVessel === 'ALL' ? '' : formatVesselName(selectedVessel);
    const rowsByVessel = {};

    Array.from(vesselNames).forEach((name) => {
      rowsByVessel[name] = {
        vessel: name,
        noonReports: 0,
        distance: 0,
        hours: 0,
        fuel: 0,
        co2: 0,
        avgSpeedWeighted: 0,
        bunkerQty: 0,
        bunkerCost: 0,
      };
    });

    (noonReports || []).forEach((report) => {
      const date = reportDate(report);
      if (!sameMonth(date, month)) return;
      const vessel = reportVessel(report);
      if (!vessel || (selected && vessel !== selected)) return;
      if (!rowsByVessel[vessel]) rowsByVessel[vessel] = { vessel, noonReports:0, distance:0, hours:0, fuel:0, co2:0, avgSpeedWeighted:0, bunkerQty:0, bunkerCost:0 };
      const data = reportData(report);
      const speed = asNumber(data.speed || data.speedMadeGood || data.speed_made_good);
      const distance = asNumber(data.distanceSailed || data.distance_sailed || data.distance);
      const fuel = asNumber(data.fuelConsumed || data.fuel_consumed)
        || asNumber(data.hfoConsumed || data.hfo_consumed)
        + asNumber(data.mdoConsumed || data.mdo_consumed)
        + asNumber(data.lsmgoConsumed || data.lsmgo_consumed);
      const hours = speed > 0 && distance > 0 ? distance / speed : asNumber(data.steamingHours || data.steaming_hours);
      rowsByVessel[vessel].noonReports += 1;
      rowsByVessel[vessel].distance += distance;
      rowsByVessel[vessel].hours += hours;
      rowsByVessel[vessel].fuel += fuel;
      rowsByVessel[vessel].co2 += fuel * CO2_FACTOR_MT_PER_MT_FUEL;
      rowsByVessel[vessel].avgSpeedWeighted += speed * Math.max(1, distance || hours || 1);
    });

    (bunkerReports || []).forEach((bunker) => {
      const date = bunkerDate(bunker);
      if (!sameMonth(date, month)) return;
      const vessel = formatVesselName(bunker.vessel);
      if (!vessel || (selected && vessel !== selected)) return;
      if (!rowsByVessel[vessel]) rowsByVessel[vessel] = { vessel, noonReports:0, distance:0, hours:0, fuel:0, co2:0, avgSpeedWeighted:0, bunkerQty:0, bunkerCost:0 };
      const qty = bunkerFuelLines(bunker).reduce((sum, fuel) => sum + asNumber(fuel.quantity), 0);
      const cost = asNumber(bunker.totalCost || bunker.total_cost)
        || bunkerFuelLines(bunker).reduce((sum, fuel) => sum + asNumber(fuel.quantity) * asNumber(fuel.pricePerMT || fuel.price_per_mt), 0);
      rowsByVessel[vessel].bunkerQty += qty;
      rowsByVessel[vessel].bunkerCost += cost;
    });

    let rows = Object.values(rowsByVessel);
    if (selected) rows = rows.filter((row) => row.vessel === selected);
    rows = rows
      .filter((row) => row.noonReports || row.bunkerQty || !selected)
      .map((row) => {
        const weightedBase = Math.max(1, row.distance || row.hours || row.noonReports || 1);
        const avgSpeed = row.avgSpeedWeighted ? row.avgSpeedWeighted / weightedBase : 0;
        const cii = row.distance > 0 ? (row.co2 * 1000000) / (4200 * row.distance) : 0;
        return {
          ...row,
          avgSpeed: Number(avgSpeed.toFixed(1)),
          fuelPerNm: row.distance > 0 ? Number((row.fuel / row.distance).toFixed(3)) : 0,
          cii: Number(cii.toFixed(2)),
          ciiGrade: ciiGrade(cii),
        };
      })
      .sort((a, b) => b.distance - a.distance || b.fuel - a.fuel || a.vessel.localeCompare(b.vessel));

    const totals = rows.reduce((sum, row) => ({
      vessels: sum.vessels + (row.noonReports || row.bunkerQty ? 1 : 0),
      noonReports: sum.noonReports + row.noonReports,
      distance: sum.distance + row.distance,
      fuel: sum.fuel + row.fuel,
      co2: sum.co2 + row.co2,
      bunkerQty: sum.bunkerQty + row.bunkerQty,
      bunkerCost: sum.bunkerCost + row.bunkerCost,
    }), { vessels:0, noonReports:0, distance:0, fuel:0, co2:0, bunkerQty:0, bunkerCost:0 });

    const fleetAvgSpeed = rows.reduce((sum, row) => sum + row.avgSpeed * Math.max(1, row.distance), 0) / Math.max(1, rows.reduce((sum, row) => sum + Math.max(1, row.distance), 0));
    const fleetCii = totals.distance > 0 ? (totals.co2 * 1000000) / (4200 * totals.distance) : 0;

    return {
      vesselOptions: Array.from(vesselNames).sort(),
      rows,
      totals: {
        ...totals,
        avgSpeed: Number(fleetAvgSpeed.toFixed(1)),
        cii: Number(fleetCii.toFixed(2)),
        ciiGrade: ciiGrade(fleetCii),
      },
    };
  }, [vessels, noonReports, bunkerReports, month, selectedVessel]);

  return (
    <div style={{ height:'100%', overflow:'auto', background:'var(--bg)', padding:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', gap:12, flexWrap:'wrap', alignItems:'flex-end', marginBottom:14 }}>
        <div>
          <div style={{ fontSize:9, color:'var(--t3)', letterSpacing:1.5, textTransform:'uppercase', marginBottom:4 }}>Fleet Analytics</div>
          <div style={{ fontFamily:'var(--syne)', fontSize:24, fontWeight:800, color:'var(--accent)' }}>Performance</div>
          <div style={{ fontSize:11, color:'var(--t2)', marginTop:4 }}>Cumulative vessel performance by month from noon reports and bunker reports.</div>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width:145 }} />
          <select value={selectedVessel} onChange={(e) => setSelectedVessel(e.target.value)} style={{ width:220 }}>
            <option value="ALL">All vessels</option>
            {model.vesselOptions.map((name) => <option key={name} value={name}>{name}</option>)}
          </select>
          <span style={{ fontSize:10, color:status.includes('unavailable') ? 'var(--yellow)' : 'var(--green)' }}>{status}</span>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(6,minmax(130px,1fr))', gap:10, marginBottom:12 }}>
        <MetricCard label="Active vessels" value={String(model.totals.vessels)} sub="with activity" />
        <MetricCard label="Noon reports" value={String(model.totals.noonReports)} sub={month} />
        <MetricCard label="Distance" value={`${number(model.totals.distance)} NM`} accent />
        <MetricCard label="Avg speed" value={`${number(model.totals.avgSpeed, 1)} kn`} />
        <MetricCard label="Fuel consumed" value={`${number(model.totals.fuel, 1)} MT`} accent />
        <MetricCard label="Bunker cost" value={currency(model.totals.bunkerCost)} />
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1.2fr .8fr', gap:12, marginBottom:12 }}>
        <ChartPanel title="Monthly Vessel Performance" subtitle="distance, fuel, and speed by vessel">
          <ResponsiveContainer width="100%" height={235}>
            <BarChart data={model.rows.slice(0, 12)} margin={{ top: 12, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="vessel" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={55} />
              <YAxis yAxisId="left" tick={{ fontSize: 9 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9 }} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="distance" name="Distance NM" fill="var(--accent)" />
              <Bar yAxisId="right" dataKey="fuel" name="Fuel MT" fill="var(--orange)" />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>
        <ChartPanel title="Efficiency Curve" subtitle={`Fleet CII ${model.totals.cii || 'TBC'} / ${model.totals.ciiGrade}`}>
          <ResponsiveContainer width="100%" height={235}>
            <LineChart data={model.rows.slice(0, 12)} margin={{ top: 12, right: 12, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="vessel" tick={{ fontSize: 9 }} interval={0} angle={-20} textAnchor="end" height={55} />
              <YAxis tick={{ fontSize: 9 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="avgSpeed" name="Avg speed kn" stroke="var(--green)" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="fuelPerNm" name="Fuel MT/NM" stroke="var(--yellow)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>

      <div style={{ background:'var(--s1)', border:'1px solid var(--b1)', borderRadius:'var(--radius)', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:980 }}>
          <thead>
            <tr style={{ background:'var(--table-head-bg)' }}>
              {['Vessel','Reports','Distance','Avg speed','Fuel','Fuel/NM','CO2','CII','Bunkered','Bunker cost'].map((head) => (
                <th key={head} style={{ textAlign:'left', padding:'9px 10px', fontSize:9, color:'var(--t2)', letterSpacing:1.2, textTransform:'uppercase', borderBottom:'1px solid var(--b1)' }}>{head}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {model.rows.map((row, index) => (
              <tr key={row.vessel} style={{ background:index % 2 === 0 ? 'var(--schedule-row-even)' : 'var(--schedule-row-odd)' }}>
                <td style={tdStrong}>{row.vessel}</td>
                <td style={td}>{row.noonReports}</td>
                <td style={td}>{number(row.distance)} NM</td>
                <td style={td}>{number(row.avgSpeed, 1)} kn</td>
                <td style={td}>{number(row.fuel, 1)} MT</td>
                <td style={td}>{number(row.fuelPerNm, 3)}</td>
                <td style={td}>{number(row.co2)} MT</td>
                <td style={td}><span style={{ color:row.ciiGrade === 'A' || row.ciiGrade === 'B' ? 'var(--green)' : row.ciiGrade === 'TBC' ? 'var(--t3)' : 'var(--yellow)', fontWeight:800 }}>{row.cii || 'TBC'} / {row.ciiGrade}</span></td>
                <td style={td}>{number(row.bunkerQty, 1)} MT</td>
                <td style={td}>{currency(row.bunkerCost)}</td>
              </tr>
            ))}
            {!model.rows.length && (
              <tr>
                <td colSpan={10} style={{ padding:18, color:'var(--t3)', textAlign:'center', fontSize:12 }}>No performance records found for the selected filter.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const td = {
  padding:'8px 10px',
  fontSize:11,
  color:'var(--t2)',
  borderBottom:'1px solid var(--b1)',
};

const tdStrong = {
  ...td,
  color:'var(--text)',
  fontWeight:700,
  fontFamily:'var(--syne)',
};
